/**
 * Aprovação do PLANO do projeto (Onda B) — distinta da aprovação da análise.
 * - planejado: configura aprovadores + envia pra aprovação.
 * - aguardando_aprovacao: status da rodada; o aprovador designado aprova/rejeita.
 * - em_execucao+: histórico read-only da decisão.
 */
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { UserCheck, Send, Plus, X, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { projetoAprovacaoAPI } from '../../../services/inovacaoAPI';
import { authAPI } from '../../../services/api';
import { mensagemErro } from '../labels';

const ESTADO_AP = {
  pendente:  { label: 'Pendente',  cor: '#f39c12', Icon: Clock },
  aprovada:  { label: 'Aprovada',  cor: '#27ae60', Icon: CheckCircle2 },
  rejeitada: { label: 'Rejeitada', cor: '#e74c3c', Icon: XCircle },
};

export default function AprovacaoPlano({ projeto, recarregar }) {
  const [usuarios, setUsuarios] = useState([]);
  const [aprovadores, setAprovadores] = useState([]);   // config (planejado)
  const [aprovacoes, setAprovacoes] = useState([]);      // rodada (aguardando+)
  const [linha, setLinha] = useState({ usuario_id: '', papel_descricao: '' });

  const planejado = projeto.estado === 'planejado';

  async function carregar() {
    try {
      if (planejado) setAprovadores(await projetoAprovacaoAPI.listarAprovadores(projeto.id));
      else setAprovacoes(await projetoAprovacaoAPI.listarAprovacoes(projeto.id));
    } catch { /* noop */ }
  }
  useEffect(() => { authAPI.listUsers().then((r) => setUsuarios(r.data)).catch(() => {}); }, []);
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [projeto.id, projeto.estado]);

  // -------- configuração (planejado) --------
  function addLinha() {
    if (!linha.usuario_id || !linha.papel_descricao.trim()) {
      toast.error('Escolha a pessoa e o papel'); return;
    }
    if (aprovadores.some((a) => a.usuario?.id === parseInt(linha.usuario_id))) {
      toast.error('Pessoa já está na lista'); return;
    }
    const u = usuarios.find((x) => x.id === parseInt(linha.usuario_id));
    setAprovadores((l) => [...l, {
      id: `tmp-${Date.now()}`, usuario: { id: u.id, nome: u.nome_completo },
      papel_descricao: linha.papel_descricao.trim(), obrigatorio: true, ordem: l.length + 1,
    }]);
    setLinha({ usuario_id: '', papel_descricao: '' });
  }
  function rmLinha(id) { setAprovadores((l) => l.filter((a) => a.id !== id)); }

  async function salvarAprovadores() {
    try {
      const payload = aprovadores.map((a, i) => ({
        usuario_id: a.usuario.id, papel_descricao: a.papel_descricao,
        obrigatorio: a.obrigatorio !== false, ordem: i + 1,
      }));
      setAprovadores(await projetoAprovacaoAPI.substituirAprovadores(projeto.id, payload));
      toast.success('Aprovadores salvos');
    } catch (e) { toast.error(mensagemErro(e, 'Erro ao salvar')); }
  }

  async function enviar() {
    if (aprovadores.length === 0) { toast.error('Adicione ao menos um aprovador'); return; }
    try {
      // garante que a lista atual está persistida antes de enviar
      await salvarAprovadores();
      await projetoAprovacaoAPI.enviar(projeto.id);
      toast.success('Enviado para aprovação. E-mail disparado aos aprovadores');
      await recarregar();
    } catch (e) { toast.error(mensagemErro(e, 'Erro ao enviar')); }
  }

  // -------- responder (aguardando) --------
  async function responder(ap, decisao) {
    let comentario = null;
    if (decisao === 'rejeitada') {
      comentario = window.prompt('Motivo da rejeição (volta pra ajuste):') || '';
      if (!comentario.trim()) { toast.error('Informe o motivo'); return; }
    }
    try {
      await projetoAprovacaoAPI.responder(ap.id, decisao, comentario);
      toast.success(decisao === 'aprovada' ? 'Aprovado' : 'Rejeitado');
      await recarregar();
    } catch (e) { toast.error(mensagemErro(e, 'Erro ao responder')); }
  }

  return (
    <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: 24, maxWidth: 760 }}>
      <h3 style={{ margin: '0 0 4px', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 8 }}>
        <UserCheck size={20} /> Aprovação do plano
      </h3>
      <p style={{ margin: '0 0 20px', color: '#7f8c8d', fontSize: '0.88rem' }}>
        Aprova o plano (fases, cronograma e horas) antes da execução. Ao aprovar, o projeto entra em
        execução e o <b>baseline trava</b>. Depois disso, mudanças entram como desvio.
      </p>

      {planejado ? (
        <>
          <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
            {aprovadores.map((a) => (
              <div key={a.id} style={linhaStyle}>
                <span style={{ flex: 1, fontWeight: 500 }}>{a.usuario?.nome}</span>
                <span style={{ color: '#7f8c8d', fontSize: '0.85rem' }}>{a.papel_descricao}</span>
                <span style={{ fontSize: '0.72rem', color: '#9b59b6', background: '#f4ecf7', padding: '2px 8px', borderRadius: 10 }}>
                  obrigatório
                </span>
                <button onClick={() => rmLinha(a.id)} style={iconBtn} title="Remover"><X size={15} /></button>
              </div>
            ))}
            {aprovadores.length === 0 && (
              <span style={{ color: '#bdc3c7', fontSize: '0.86rem' }}>Nenhum aprovador ainda. Adicione abaixo.</span>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <select value={linha.usuario_id} onChange={(e) => setLinha({ ...linha, usuario_id: e.target.value })} style={input}>
              <option value="">Escolha a pessoa</option>
              {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nome_completo}</option>)}
            </select>
            <input value={linha.papel_descricao} onChange={(e) => setLinha({ ...linha, papel_descricao: e.target.value })}
                   placeholder="Papel (ex: Sponsor, Diretor)" style={{ ...input, flex: 1 }} />
            <button onClick={addLinha} style={{ ...botao, background: '#ecf0f1', color: '#34495e' }}>
              <Plus size={15} /> Adicionar
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={salvarAprovadores} style={{ ...botao, background: '#ecf0f1', color: '#34495e' }}>
              Salvar lista
            </button>
            <button onClick={enviar} style={{ ...botao, background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)' }}>
              <Send size={15} /> Enviar para aprovação
            </button>
          </div>
        </>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {aprovacoes.map((a) => {
            const m = ESTADO_AP[a.estado] || ESTADO_AP.pendente;
            return (
              <div key={a.id} style={linhaStyle}>
                <span style={{ flex: 1, fontWeight: 500 }}>{a.aprovador?.nome}</span>
                <span style={{ color: '#7f8c8d', fontSize: '0.85rem' }}>{a.papel_descricao}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: m.cor,
                               border: `1px solid ${m.cor}`, borderRadius: 10, padding: '2px 8px', fontSize: '0.74rem' }}>
                  <m.Icon size={12} /> {m.label}
                </span>
                {a.pode_responder && (
                  <span style={{ display: 'inline-flex', gap: 6 }}>
                    <button onClick={() => responder(a, 'aprovada')} style={{ ...botao, background: '#27ae60', color: '#fff', padding: '5px 10px' }}>
                      <CheckCircle2 size={14} /> Aprovar
                    </button>
                    <button onClick={() => responder(a, 'rejeitada')} style={{ ...botao, background: '#e74c3c', color: '#fff', padding: '5px 10px' }}>
                      <XCircle size={14} /> Rejeitar
                    </button>
                  </span>
                )}
                {a.comentario && <span style={{ color: '#95a5a6', fontSize: '0.78rem', fontStyle: 'italic' }}>"{a.comentario}"</span>}
              </div>
            );
          })}
          {aprovacoes.length === 0 && (
            <span style={{ color: '#bdc3c7', fontSize: '0.86rem' }}>Sem rodada de aprovação registrada.</span>
          )}
        </div>
      )}
    </div>
  );
}

const linhaStyle = { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                     border: '1px solid #eef2f4', borderRadius: 8, background: '#fbfdfe', flexWrap: 'wrap' };
const input = { padding: 8, borderRadius: 6, border: '1px solid #ddd', fontSize: '0.86rem', fontFamily: 'inherit' };
const botao = { border: 'none', borderRadius: 7, padding: '9px 16px', cursor: 'pointer', fontWeight: 600,
                fontSize: '0.86rem', display: 'inline-flex', alignItems: 'center', gap: 6 };
const iconBtn = { background: 'none', border: 'none', color: '#bdc3c7', cursor: 'pointer', padding: 2 };
